import React, { useEffect, useRef, useState } from 'react'
import '../styles/Testimonials.css'
import { T } from './Translation'

function Testimonials() {
  const testimonialsRef = useRef(null)
  const trackRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // This will be your testimonials data - you can add more in the future
  const testimonials = [
    {
      textKey: 'testimonial1Text',
      authorKey: 'testimonial1Author',
      avatar: '👰'
    },
    {
      textKey: 'testimonial2Text',
      authorKey: 'testimonial2Author',
      avatar: '🤵'
    },
    {
      textKey: 'testimonial3Text',
      authorKey: 'testimonial3Author',
      avatar: '💑'
    },
    // You can add more testimonials here in the future
    {
      textKey: 'testimonial1Text', // Duplicate for demo - replace with real data
      authorKey: 'testimonial1Author',
      avatar: '🎉'
    },
    {
      textKey: 'testimonial2Text', // Duplicate for demo - replace with real data
      authorKey: 'testimonial2Author',
      avatar: '💒'
    }
  ]

  useEffect(() => {
    if (window.location.hash === '#testimonials' && testimonialsRef.current) {
      testimonialsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (!isAutoPlaying) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, testimonials.length - 2))
    }, 4000)

    return () => clearInterval(interval)
  }, [isAutoPlaying, testimonials.length])

  const nextSlide = () => {
    if (isAnimating) return
    setIsAutoPlaying(false)
    setIsAnimating(true)
    
    // Add slide animation
    if (trackRef.current) {
      trackRef.current.classList.add('sliding-left')
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % Math.max(1, testimonials.length - 2))
        trackRef.current?.classList.remove('sliding-left')
        trackRef.current?.classList.add('snap-back')
        setTimeout(() => {
          trackRef.current?.classList.remove('snap-back')
          setIsAnimating(false)
        }, 300)
      }, 200)
    }
  }

  const prevSlide = () => {
    if (isAnimating) return
    setIsAutoPlaying(false)
    setIsAnimating(true)
    
    // Add slide animation
    if (trackRef.current) {
      trackRef.current.classList.add('sliding-right')
      setTimeout(() => {
        setCurrentIndex((prev) => (prev - 1 + Math.max(1, testimonials.length - 2)) % Math.max(1, testimonials.length - 2))
        trackRef.current?.classList.remove('sliding-right')
        trackRef.current?.classList.add('snap-back')
        setTimeout(() => {
          trackRef.current?.classList.remove('snap-back')
          setIsAnimating(false)
        }, 300)
      }, 200)
    }
  }

  // Touch/swipe handlers with visual feedback
  const handleTouchStart = (e) => {
    setIsAutoPlaying(false)
    setTouchStart(e.targetTouches[0].clientX)
    if (trackRef.current) {
      trackRef.current.classList.add('swiping')
    }
  }

  const handleTouchMove = (e) => {
    const touchCurrent = e.targetTouches[0].clientX
    setTouchEnd(touchCurrent)
    
    // Real-time visual feedback during swipe
    if (touchStart && trackRef.current) {
      const distance = touchStart - touchCurrent
      const maxDistance = 100 // Limit the visual movement
      const clampedDistance = Math.max(-maxDistance, Math.min(maxDistance, distance))
      trackRef.current.style.transform = `translateX(${-clampedDistance * 0.3}px)`
    }
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (trackRef.current) {
      trackRef.current.classList.remove('swiping')
      trackRef.current.style.transform = '' // Reset transform
      
      if (isLeftSwipe) {
        nextSlide()
      } else if (isRightSwipe) {
        prevSlide()
      } else {
        // Snap back if swipe wasn't far enough
        trackRef.current.classList.add('snap-back')
        setTimeout(() => {
          trackRef.current?.classList.remove('snap-back')
        }, 300)
      }
    }
    
    setTouchStart(0)
    setTouchEnd(0)
  }

  const getVisibleTestimonials = () => {
    if (testimonials.length < 3) return testimonials
    
    const result = []
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length
      result.push({ ...testimonials[index], position: i })
    }
    return result
  }

  return (
    <section
      id="testimonials"
      ref={testimonialsRef}
      className="testimonials-section"
    >
      <h2>
        <T k="testimonialsTitle" />
      </h2>
      <div className="testimonials-container">
        <div className="testimonials-carousel">
          <div 
            ref={trackRef}
            className="testimonials-track"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {getVisibleTestimonials().map((testimonial, index) => (
              <div 
                key={`${currentIndex}-${index}`}
                className={`testimonial-card ${testimonial.position === 1 ? 'center' : testimonial.position === 0 ? 'left' : 'right'}`}
              >
                <div className="quote-icon">"</div>
                <p className="testimonial-text">
                  <T k={testimonial.textKey} />
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <span>{testimonial.avatar}</span>
                  </div>
                  <cite className="author-name">
                    <T k={testimonial.authorKey} />
                  </cite>
                </div>
              </div>
            ))}
          </div>
          
          {testimonials.length > 3 && (
            <>
              <button 
                className="carousel-arrow prev-arrow" 
                onClick={prevSlide}
                aria-label="Previous testimonials"
              >
                ←
              </button>
              
              <button 
                className="carousel-arrow next-arrow" 
                onClick={nextSlide}
                aria-label="Next testimonials"
              >
                →
              </button>
            </>
          )}
        </div>
        
        {testimonials.length > 3 && (
          <div className="carousel-dots">
            {Array.from({ length: Math.max(1, testimonials.length - 2) }, (_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsAutoPlaying(false)
                  setCurrentIndex(index)
                }}
                aria-label={`Go to testimonial set ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default Testimonials
